/**
 * What changed inside one module, once its row is opened.
 *
 * Identity fields first, then the sections touched, the assessment tools on
 * either side, the placements when the module moved, and last the raw field
 * changes for a reader who wants them.
 *
 * @module components/portfolio/comparison/ModuleChangeDetail
 */

import React from "react";

import { ChangeValues, PrimitiveTransition } from "@/components/portfolio/comparison/change-values";
import type {
  AcademicChange,
  ModuleDifference,
} from "@/components/portfolio/comparison/diff-types";
import { changeKindTone } from "@/components/portfolio/comparison/module-status";
import { humanizeIdentifier, humanizeRevisionPath } from "@/components/shared/diff-labels";
import {
  Accordion,
  AccordionItem,
  Badge,
  Divider,
  Eyebrow,
  Heading,
  KeyValue,
  MutedText,
  Text,
} from "@/components/ui";
import type { Module } from "@/types";

export interface ModuleChangeDetailProps {
  module: ModuleDifference;
  /** The academic changes, keyed `path:kind`, that describe this module's fields. */
  projectedChanges: ReadonlyMap<string, AcademicChange>;
}

/** The assessment tools of one revision of a module, as one readable line. */
function assessmentToolSummary(module: Module | undefined): string {
  const assessments = module?.assessments ?? [];
  if (assessments.length === 0) {
    return "No assessments recorded";
  }
  return assessments
    .map((assessment) => {
      const label = assessment.type || assessment.title || "Unspecified assessment";
      const weighting = assessment.weighting ?? assessment.weight;
      return weighting === undefined ? label : `${weighting}% ${label}`;
    })
    .join(", ");
}

/** The two assessment lines, or nothing when they say the same thing. */
function AssessmentTools({ module }: { module: ModuleDifference }): React.ReactElement | null {
  if (!module.before || !module.after) {
    return null;
  }
  const before = assessmentToolSummary(module.before);
  const after = assessmentToolSummary(module.after);
  if (before === after) {
    return null;
  }
  return (
    <>
      <Divider className="mt-4" />
      <div className="py-3 text-md">
        <Heading level={4}>Assessment tools</Heading>
        <div className="mt-2 grid gap-3 md:grid-cols-2">
          <div>
            <Eyebrow as="div">Earlier</Eyebrow>
            <div className="mt-1">{before}</div>
          </div>
          <div>
            <Eyebrow as="div">Later</Eyebrow>
            <div className="mt-1">{after}</div>
          </div>
        </div>
      </div>
      <Divider />
    </>
  );
}

/**
 * @example
 * ```tsx
 * <ModuleChangeDetail module={difference} projectedChanges={byPathAndKind} />
 * ```
 */
export function ModuleChangeDetail({
  module,
  projectedChanges,
}: ModuleChangeDetailProps): React.ReactElement {
  const { before, after } = module;
  const identity =
    before &&
    after &&
    [
      { field: "Title", before: before.title, after: after.title },
      { field: "Code", before: before.code, after: after.code },
      { field: "Credits", before: before.credits, after: after.credits },
    ].filter((row) => row.before !== row.after);
  const moved = module.statuses.some(
    (status) => status === "new" || status === "removed" || status === "moved",
  );

  return (
    <>
      {identity && identity.length > 0 ? (
        <KeyValue
          valueWeight="prose"
          entries={identity.map((row) => ({
            label: row.field,
            value: (
              <span
                data-testid={
                  row.field === "Credits"
                    ? `comparison-module-${module.id}-credits-change`
                    : undefined
                }
              >
                <PrimitiveTransition before={row.before} after={row.after} />
              </span>
            ),
          }))}
        />
      ) : null}
      {module.changedSections.length > 0 ? (
        <Text className="mt-3">
          <span className="font-medium">Changed:</span> {module.changedSections.join(", ")}
        </Text>
      ) : null}
      <AssessmentTools module={module} />
      {moved ? (
        <div className="mt-3 grid gap-3 text-md md:grid-cols-2">
          <div>
            <Eyebrow as="div">Earlier placement</Eyebrow>
            <div>{module.beforePlacements.join("; ") || "Not placed"}</div>
          </div>
          <div>
            <Eyebrow as="div">Later placement</Eyebrow>
            <div>{module.afterPlacements.join("; ") || "Not placed"}</div>
          </div>
        </div>
      ) : null}
      {module.fieldChanges.length > 0 ? (
        <Accordion id={`comparison-module-${module.id}-fields`} className="mt-4">
          <AccordionItem
            eventKey={`comparison-module-${module.id}-fields`}
            headingLevel={4}
            title="Detailed field changes"
            subtitle={`${module.fieldChanges.length} ${module.fieldChanges.length === 1 ? "change" : "changes"}`}
            subtitlePosition="right"
            data-testid={`comparison-module-${module.id}-fields`}
          >
            <div className="divide-y divide-base-300">
              {module.fieldChanges.map((change) => {
                const projected = projectedChanges.get(`${change.path}:${change.kind}`);
                return (
                  <div key={`${change.path}:${change.kind}`} className="py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge size="sm" tone={changeKindTone(change.kind)}>
                        {humanizeIdentifier(change.kind)}
                      </Badge>
                      <span className="text-md font-medium">
                        {projected?.description ??
                          humanizeRevisionPath(change.path).split(" › ").at(-1)}
                      </span>
                    </div>
                    <ChangeValues change={projected ?? change} />
                  </div>
                );
              })}
            </div>
          </AccordionItem>
        </Accordion>
      ) : null}
      {identity?.length === 0 &&
      module.changedSections.length === 0 &&
      module.fieldChanges.length === 0 &&
      !moved ? (
        <MutedText>No recorded differences in this module.</MutedText>
      ) : null}
    </>
  );
}
